import axios from "axios";
import { APP_ENV } from "@constants";
import { FirebaseService } from "@services";
import { ANALYTICS_EVENTS_ACTIONS } from "@constants/analyticsEvents";
import AnalyticsService from "@services/AnalyticsService";

/* For local testing, comment out the conditions below that check APP_ENV. Comment the APP_ENV import as well.
 * IMPORTANT: UNDO THIS CHANGE BEFORE COMMITTING TO THE REMOTE REPO */

const logError = async (error, stackTrace, userId) => {
	const {
		navigator: { userAgent },
	} = window;

	let text = `*Error Caught* - <${window.location.href}>\n\n*User Agent:* _${userAgent}_\n\n*UserId:* _${userId}_\n`;
	text += `\`\`\`${error.name}: ${error.message}\n${stackTrace}\`\`\``;

	// Log event to Google Analytics
	AnalyticsService.trackEvent({
		action: ANALYTICS_EVENTS_ACTIONS.UI_CRASH,
		label: error.name,
		user: {
			Id: userId,
		},
		customParameters: {
			text,
			url: window.location.href,
			userAgent,
			errorMessage: error.message,
			stack: stackTrace,
		},
	});

	// Get remote config values and send a message to the error log channel in Slack if debugging is enabled
	const debugErrors = await FirebaseService.getConfigValue("error_debug_enabled", "bool");
	const errorLogUrl = await FirebaseService.getConfigValue("error_report_url", "string");
	if ((APP_ENV === "production" || APP_ENV === "demo") && debugErrors) {
		axios.post(
			errorLogUrl,
			{ text },
			{ headers: { "Content-type": "application/x-www-form-urlencoded" } },
		);
	}
};

const handleErrorResponse = async error => {
	// Get remote config values and send a message to the error log channel in Slack if debugging is enabled
	const debugRequests = await FirebaseService.getConfigValue("network_debug_enabled", "bool");
	const errorLogUrl = await FirebaseService.getConfigValue("error_report_url", "string");
	if (
		(APP_ENV === "production" || APP_ENV === "demo") &&
		!axios.isCancel(error) &&
		debugRequests
	) {
		const {
			userId,
			navigator: { userAgent },
		} = window;
		const { name, message, config = {}, request = {}, response = {} } = error;
		const { baseURL, method = "unknown", url, data: payload, params = {} } = config;
		const { status } = request;
		const { data = {} } = response;

		// TODO: Add User ID/other information once available appwide
		const blocks = [
			{
				type: "header",
				text: { type: "plain_text", text: "Network Error" },
			},
			{
				type: "section",
				text: { type: "plain_text", text: `${name}: ${message}` },
			},
			{
				type: "section",
				text: { type: "mrkdwn", text: `Error occurred on <${window.location.href}>` },
			},
			{
				type: "section",
				text: { type: "mrkdwn", text: `*User Agent*\n_${userAgent}_` },
			},
		];

		if (userId) {
			blocks.push({
				type: "section",
				text: { type: "mrkdwn", text: `*UserId*\n_${userId}_` },
			});
		}

		const requestBlocks = [
			{ type: "divider" },
			{
				type: "section",
				text: { type: "mrkdwn", text: `*Endpoint*\n\`${baseURL}/${url}\`` },
			},
			{
				type: "section",
				fields: [
					{ type: "mrkdwn", text: `*Method*: ${method.toUpperCase()}` },
					{ type: "mrkdwn", text: `*Status Code:* ${status}` },
				],
			},
		];
		if (Object.keys(params).length > 0) {
			requestBlocks.push({
				type: "section",
				text: {
					type: "mrkdwn",
					text: `*Query Params*\n\`\`\`${JSON.stringify(params, null, "\t")}\`\`\``,
				},
			});
		}
		if (payload) {
			requestBlocks.push({
				type: "section",
				text: {
					type: "mrkdwn",
					text: `*Payload*\n\`\`\`${JSON.stringify(JSON.parse(payload), null, "\t")}\`\`\``,
				},
			});
		}

		const responseBlocks = [
			{ type: "divider" },
			{
				type: "section",
				text: { type: "mrkdwn", text: "*Response*" },
			},
			{
				type: "section",
				text: { type: "mrkdwn", text: `\`\`\`${JSON.stringify(data, null, "\t")}\`\`\`` },
			},
		];

		axios.post(
			errorLogUrl,
			{
				text: "Network error detected",
				blocks: [...blocks, ...requestBlocks, ...responseBlocks],
			},
			{ headers: { "Content-type": "application/x-www-form-urlencoded" } },
		);
	}

	return Promise.reject(error);
};

export { logError, handleErrorResponse };
